import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {
  ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS,
  ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS,
  ACCOUNT_TURNOVER_RENT_CLASS,
  ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS,
  ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS,
} from './accounting/accountClasses';
import { type CalendarDate, isAfter, isWithinInterval, toCalendarDate, today } from './dateutils';
import { roundAtSecondDecimal } from './numbers';
import { isNilOrEmpty } from './objectUtils';
import {
  AccountLabel,
  Charge,
  ChargeDTO,
  ChargeStatus,
  Language,
  LeaseVariousOperation,
  LeaseVariousOperationType,
  Posting,
  Statement,
  StatementStatus,
} from './types';

export type LvoLike = Pick<LeaseVariousOperation, 'operationType' | 'unitId' | 'amount' | 'id'>;

interface EndableLvo {
  endDate?: string | null;
}

type SortableLeaseVariousOperation = Pick<LeaseVariousOperation, 'operationType' | 'accountLabelIds' | 'label'>;
export type ChargeLvoWithAccountLabels = Pick<LeaseVariousOperation, 'id' | 'accountLabelIds'>;
export type ChargePocketLvo = Pick<
  LeaseVariousOperation,
  'accountLabelIds' | 'amount' | 'id' | 'label' | 'operationType' | 'unitId'
>;

export const GENERAL_POCKET_KEY = 'GENERAL_CHARGES';

type StatementPocketSelection = Pick<Statement, 'status' | 'selectedPocketKeys'>;

/** A provision without linked accounts is the general pocket: statements select it by the general key, not its id. */
export const getChargePocketKey = (charge: Pick<LeaseVariousOperation, 'id' | 'accountLabelIds'>): string =>
  isNilOrEmpty(charge.accountLabelIds) ? GENERAL_POCKET_KEY : charge.id;

export const isOngoingStatementIncludingPocket = (statement: StatementPocketSelection, pocketKey: string): boolean =>
  statement.status === StatementStatus.TO_VALIDATE &&
  (isNilOrEmpty(statement.selectedPocketKeys) || (statement.selectedPocketKeys ?? []).includes(pocketKey));
export const CHARGE_POCKET_OPTION_PREFIX = 'pocket:';

export enum ChargeOrder {
  GENERAL_PROVISIONED_CHARGE = 0,
  ACCOUNT_LABELLED_PROVISIONED_CHARGE = 1,
  FIXED_CHARGE = 2,
  NOT_A_CHARGE = 3,
}

export const getChargeOrder = (leaseVariousOperation: SortableLeaseVariousOperation): ChargeOrder => {
  switch (leaseVariousOperation.operationType) {
    case LeaseVariousOperationType.PROVISIONED_CHARGE:
      return isNilOrEmpty(leaseVariousOperation.accountLabelIds)
        ? ChargeOrder.GENERAL_PROVISIONED_CHARGE
        : ChargeOrder.ACCOUNT_LABELLED_PROVISIONED_CHARGE;
    case LeaseVariousOperationType.FIXED_CHARGE:
      return ChargeOrder.FIXED_CHARGE;
    default:
      return ChargeOrder.NOT_A_CHARGE;
  }
};

export const CHARGE_POSTING_SORT_BASE_CLASS = ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS;

export const getLeaseVariousOperationSortClass = (leaseVariousOperation: SortableLeaseVariousOperation): number => {
  switch (leaseVariousOperation.operationType) {
    case LeaseVariousOperationType.DISCOUNT:
      return ACCOUNT_TURNOVER_RENT_CLASS;
    case LeaseVariousOperationType.PROVISIONED_CHARGE:
    case LeaseVariousOperationType.FIXED_CHARGE:
      return CHARGE_POSTING_SORT_BASE_CLASS + getChargeOrder(leaseVariousOperation);
    case LeaseVariousOperationType.SERVICE:
      return ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS;
    default:
      return ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS;
  }
};

export const compareChargeLabels = (
  leftLabel: string | null | undefined,
  rightLabel: string | null | undefined,
  language: Language
): number => (leftLabel ?? '').localeCompare(rightLabel ?? '', language);

export const sortLeaseVariousOperationsForDisplay = <T extends SortableLeaseVariousOperation>(
  leaseVariousOperations: T[],
  language: Language
): T[] =>
  [...leaseVariousOperations].sort((leftLeaseVariousOperation, rightLeaseVariousOperation) => {
    const sortClassDifference =
      getLeaseVariousOperationSortClass(leftLeaseVariousOperation) -
      getLeaseVariousOperationSortClass(rightLeaseVariousOperation);
    if (sortClassDifference !== 0) {
      return sortClassDifference;
    }
    return compareChargeLabels(leftLeaseVariousOperation.label, rightLeaseVariousOperation.label, language);
  });

export const getChargeLvoLabel = (lvo: Pick<LeaseVariousOperation, 'label'>, fallbackLabel: string): string =>
  isNilOrEmpty(lvo.label) ? fallbackLabel : lvo.label;

export const getChargesFromDto = (chargeDtos: ChargeDTO[]): Charge[] =>
  chargeDtos.map((chargeDto) => ({
    ...chargeDto,
    postings: chargeDto.postings?.items,
  })) as Charge[];

export const sumChargeAmounts = (charges: Array<{ amount?: number | null }>): number =>
  roundAtSecondDecimal(charges.reduce((total, charge) => total + (charge.amount ?? 0), 0));

/**
 * The initial charge price is only asked for a fixed charge on an indexed lease that started in the
 * past, so most charges keep the 0 the form defaults to. That 0 means "not encoded", not "started
 * free", and every reader of it — validation, guaranty, indexation base — has to agree on that.
 */
export const getChargeInitialAmount = (charge: { initialAmount?: number | null; amount?: number | null }): number =>
  isNil(charge.initialAmount) || charge.initialAmount === 0 ? (charge.amount ?? 0) : charge.initialAmount;

export const sumFixedChargeAmounts = (
  charges: Array<{ type: LeaseVariousOperationType; amount?: number | null }>
): number =>
  roundAtSecondDecimal(
    charges.reduce(
      (total, charge) =>
        charge.type === LeaseVariousOperationType.FIXED_CHARGE ? total + (charge.amount ?? 0) : total,
      0
    )
  );

export const isFixedChargeLvo = (leaseVariousOperation: LvoLike): boolean =>
  leaseVariousOperation.operationType === LeaseVariousOperationType.FIXED_CHARGE;

export const isProvisionedChargeLvo = (leaseVariousOperation: LvoLike): boolean =>
  leaseVariousOperation.operationType === LeaseVariousOperationType.PROVISIONED_CHARGE;

export const isChargeLvo = (leaseVariousOperation: LvoLike): boolean =>
  leaseVariousOperation.operationType === LeaseVariousOperationType.FIXED_CHARGE ||
  leaseVariousOperation.operationType === LeaseVariousOperationType.PROVISIONED_CHARGE;

export const isActiveLeaseVariousOperation = (leaseVariousOperation: EndableLvo): boolean =>
  !isNil(leaseVariousOperation.endDate) && isAfter(toCalendarDate(leaseVariousOperation.endDate), today());

export type DatedAmountLvo = Pick<LeaseVariousOperation, 'amount' | 'leaseVariousOperationHistories'>;

export const getLeaseVariousOperationAmountAt = (leaseVariousOperation: DatedAmountLvo, date: CalendarDate): number =>
  leaseVariousOperation.leaseVariousOperationHistories?.find((history) =>
    isWithinInterval(date, { end: toCalendarDate(history.periodTo), start: toCalendarDate(history.periodFrom) })
  )?.amount ?? leaseVariousOperation.amount;

export const isFixedChargeLvoForUnit = (leaseVariousOperation: LvoLike, unitId: string): boolean =>
  leaseVariousOperation.unitId === unitId &&
  leaseVariousOperation.operationType === LeaseVariousOperationType.FIXED_CHARGE;

export const isProvisionedChargeLvoForUnit = (leaseVariousOperation: LvoLike, unitId: string): boolean =>
  leaseVariousOperation.unitId === unitId &&
  leaseVariousOperation.operationType === LeaseVariousOperationType.PROVISIONED_CHARGE;

export const getChargeLvosForUnit = <T extends ChargePocketLvo>(
  leaseVariousOperations: T[],
  unitId: string,
  language: Language
): T[] =>
  sortLeaseVariousOperationsForDisplay(
    leaseVariousOperations.filter(
      (leaseVariousOperation) => isChargeLvo(leaseVariousOperation) && leaseVariousOperation.unitId === unitId
    ),
    language
  );

export const isChargeOperationType = (operationType: LeaseVariousOperationType): boolean =>
  operationType === LeaseVariousOperationType.FIXED_CHARGE ||
  operationType === LeaseVariousOperationType.PROVISIONED_CHARGE;

export const getChargeLvosWithAccountLabels = <T extends LvoLike & Pick<LeaseVariousOperation, 'accountLabelIds'>>(
  variousOperations: T[] | null | undefined
): T[] => (variousOperations ?? []).filter((lvo) => isChargeLvo(lvo) && !isNilOrEmpty(lvo.accountLabelIds));

export const buildAccountLabelIdToChargeLvoMap = <T extends ChargeLvoWithAccountLabels>(
  chargeLvos: T[]
): Map<string, T> =>
  chargeLvos.reduce((accumulator, lvo) => {
    (lvo.accountLabelIds ?? []).forEach((accountLabelId) => {
      if (!accumulator.has(accountLabelId)) {
        accumulator.set(accountLabelId, lvo);
      }
    });
    return accumulator;
  }, new Map<string, T>());

export interface ChargePocketOption {
  key: string;
  label: string;
  displayLabel: string;
  leaseVariousOperationId: string;
  accountLabelId: string | null;
  amount: number;
}

export interface TransactionTypeOptions {
  accountLabels: AccountLabel[];
  pockets: ChargePocketOption[];
  showPockets: boolean;
  impliedLeaseVariousOperationIdByAccountLabelId: Map<string, string>;
}

const getFallbackClassForChargeLvo = (leaseVariousOperation: LvoLike): number =>
  isFixedChargeLvo(leaseVariousOperation)
    ? ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS
    : ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS;

const getAccountLabelIdForChargeLvo = (
  leaseVariousOperation: LvoLike,
  accountLabels: AccountLabel[]
): string | null => {
  const fallbackClass = getFallbackClassForChargeLvo(leaseVariousOperation);
  const fallbackAccountLabel = accountLabels.find((accountLabel) => accountLabel.class === fallbackClass);
  return fallbackAccountLabel?.id ?? null;
};

export const getTransactionTypeOptionsForUnit = (
  accountLabels: AccountLabel[],
  leaseVariousOperations: ChargePocketLvo[],
  unitId: string,
  language: Language,
  fallbackProvisionedChargeLabel: string,
  fallbackFixedChargeLabel: string
): TransactionTypeOptions => {
  const chargeLvosForUnit = getChargeLvosForUnit(leaseVariousOperations, unitId, language);
  const showPockets = chargeLvosForUnit.length > 1;
  const impliedLeaseVariousOperationIdByAccountLabelId = new Map<string, string>();

  if (!showPockets && chargeLvosForUnit.length === 1) {
    const soleChargeLvo = chargeLvosForUnit[0];
    const impliedClass = getFallbackClassForChargeLvo(soleChargeLvo);
    accountLabels.forEach((accountLabel) => {
      if (accountLabel.class === impliedClass) {
        impliedLeaseVariousOperationIdByAccountLabelId.set(accountLabel.id, soleChargeLvo.id);
      }
    });
  }

  const visibleAccountLabels = showPockets
    ? accountLabels.filter(
        (accountLabel) =>
          ![ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS, ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS].includes(
            accountLabel.class
          )
      )
    : accountLabels;

  let provisionedFallbackLabelIndex = 0;
  let fixedFallbackLabelIndex = 0;
  const pockets: ChargePocketOption[] = showPockets
    ? chargeLvosForUnit.map((leaseVariousOperation) => {
        const usesFallbackLabel = isNilOrEmpty(leaseVariousOperation.label);
        const isFixedCharge = isFixedChargeLvo(leaseVariousOperation);
        if (usesFallbackLabel && isFixedCharge) {
          fixedFallbackLabelIndex += 1;
        }
        if (usesFallbackLabel && !isFixedCharge) {
          provisionedFallbackLabelIndex += 1;
        }
        const fallbackChargeLabel = isFixedCharge ? fallbackFixedChargeLabel : fallbackProvisionedChargeLabel;
        const fallbackLabelIndex = isFixedCharge ? fixedFallbackLabelIndex : provisionedFallbackLabelIndex;
        const label = getChargeLvoLabel(leaseVariousOperation, fallbackChargeLabel);
        return {
          key: `${CHARGE_POCKET_OPTION_PREFIX}${leaseVariousOperation.id}`,
          label,
          displayLabel: usesFallbackLabel ? `${label} - ${fallbackLabelIndex} -` : label,
          leaseVariousOperationId: leaseVariousOperation.id,
          accountLabelId: getAccountLabelIdForChargeLvo(leaseVariousOperation, accountLabels),
          amount: leaseVariousOperation.amount,
        };
      })
    : [];

  return {
    accountLabels: visibleAccountLabels,
    pockets,
    showPockets,
    impliedLeaseVariousOperationIdByAccountLabelId,
  };
};

export interface StatementPocket<TLvo = LeaseVariousOperation> {
  key: string;
  leaseVariousOperation: TLvo | null;
  postings: Posting[];
}

export const getPocketKeyForPosting = (
  posting: Pick<Posting, 'accountLabelId'>,
  accountLabelIdToChargeLvoMap: Map<string, Pick<LeaseVariousOperation, 'id'>>
): string => {
  const lvo = isNil(posting.accountLabelId) ? undefined : accountLabelIdToChargeLvoMap.get(posting.accountLabelId);
  return isNil(lvo) ? GENERAL_POCKET_KEY : lvo.id;
};

const groupPostingsIntoPockets = <TLvo extends Pick<LeaseVariousOperation, 'id'>>(
  postings: Posting[],
  chargeLvos: TLvo[],
  findLvoForPosting: (posting: Posting) => TLvo | undefined
): StatementPocket<TLvo>[] => {
  const generalPostings: Posting[] = [];
  const postingsByLvoId = new Map<string, Posting[]>();
  postings.forEach((posting) => {
    const lvo = findLvoForPosting(posting);
    if (isNil(lvo)) {
      generalPostings.push(posting);
      return;
    }
    const existingPostings = postingsByLvoId.get(lvo.id);
    if (isNil(existingPostings)) {
      postingsByLvoId.set(lvo.id, [posting]);
      return;
    }
    existingPostings.push(posting);
  });
  const pockets: StatementPocket<TLvo>[] = [];
  if (!isEmpty(generalPostings)) {
    pockets.push({ key: GENERAL_POCKET_KEY, leaseVariousOperation: null, postings: generalPostings });
  }
  chargeLvos.forEach((lvo) => {
    const lvoPostings = postingsByLvoId.get(lvo.id);
    if (!isNil(lvoPostings)) {
      pockets.push({ key: lvo.id, leaseVariousOperation: lvo, postings: lvoPostings });
    }
  });
  return pockets;
};

export const groupPostingsByPocket = <TLvo extends ChargeLvoWithAccountLabels>(
  postings: Posting[],
  chargeLvos: TLvo[]
): StatementPocket<TLvo>[] => {
  const accountLabelIdToChargeLvoMap = buildAccountLabelIdToChargeLvoMap(chargeLvos);
  return groupPostingsIntoPockets(postings, chargeLvos, (posting) =>
    isNil(posting.accountLabelId) ? undefined : accountLabelIdToChargeLvoMap.get(posting.accountLabelId)
  );
};

export const groupProvisionsByPocket = <TLvo extends Pick<LeaseVariousOperation, 'id'>>(
  provisions: Posting[],
  chargeLvos: TLvo[]
): StatementPocket<TLvo>[] =>
  groupPostingsIntoPockets(provisions, chargeLvos, (posting) =>
    isNil(posting.leaseVariousOperationId)
      ? undefined
      : chargeLvos.find((lvo) => lvo.id === posting.leaseVariousOperationId)
  );

export const filterPostingsBySelectedPocketKeys = <T extends Pick<Posting, 'accountLabelId'>>(
  postings: T[],
  chargeLvos: ChargeLvoWithAccountLabels[],
  selectedPocketKeys: string[] | null | undefined
): T[] => {
  if (isNil(selectedPocketKeys) || isEmpty(selectedPocketKeys)) {
    return postings;
  }
  const accountLabelIdToChargeLvoMap = buildAccountLabelIdToChargeLvoMap(chargeLvos);
  return postings.filter((posting) => {
    const lvo = isNil(posting.accountLabelId) ? undefined : accountLabelIdToChargeLvoMap.get(posting.accountLabelId);
    const pocketKey = isNil(lvo) ? GENERAL_POCKET_KEY : lvo.id;
    return selectedPocketKeys.includes(pocketKey);
  });
};

export const filterProvisionsBySelectedPocketKeys = <T extends Pick<Posting, 'leaseVariousOperationId'>>(
  provisions: T[],
  chargeLvos: ChargeLvoWithAccountLabels[],
  selectedPocketKeys: string[] | null | undefined
): T[] => {
  if (isNil(selectedPocketKeys) || isEmpty(selectedPocketKeys)) {
    return provisions;
  }
  const pocketLvoIds = chargeLvos.reduce<Set<string>>((accumulator, lvo) => {
    if (!isNilOrEmpty(lvo.accountLabelIds)) {
      accumulator.add(lvo.id);
    }
    return accumulator;
  }, new Set<string>());
  return provisions.filter((provision) => {
    const pocketKey =
      isNil(provision.leaseVariousOperationId) || !pocketLvoIds.has(provision.leaseVariousOperationId)
        ? GENERAL_POCKET_KEY
        : provision.leaseVariousOperationId;
    return selectedPocketKeys.includes(pocketKey);
  });
};

export type OpenInvoiceChargeLike = Pick<Charge, 'paid' | 'status'>;

export const isOpenInvoiceCharge = (charge: OpenInvoiceChargeLike): boolean =>
  !charge.paid && (charge.status === ChargeStatus.TO_AFFECT || charge.status === ChargeStatus.AFFECTED);
